import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { REVERSE_STATUS_MAPPER, STATUS_TO_CANDIDATE_MAPPER } from "../constants/OutreachedJobs";
import PageLayout from "./ui/PageLayout";
import { REDIRECTION_LINKS } from "../constants/common";
import PageCard from "./ui/PageCard";
import Tabs from "./ui/Tabs";
import { OUTREACHED_TABS, OUTREACHED_TABS_STATUS_MAPPER } from "../constants/OutreachedCandidates";
import { useQueryParams } from "../hooks/useQueryParams";
import CustomTable from "./ui/DataTable";
import Tag from "./ui/Tag";
import { FaLinkedin } from "react-icons/fa";
import Pagination from "./ui/Pagination";
import { useSelector } from "react-redux";
import { selectCustomPagination } from "../features/paginationStore";
import useFetch from "../hooks/useFetch";
import { TailSpin } from "react-loader-spinner";
import CandidateCard from "./ui/CandidateCard";
import CardTooltip from "./ui/CardTooltip";
import EmptyScreen from "./ui/EmptyScreen";
import Button from "./ui/Button";
import { getNewPageLink, getNewSourcedCandidateObject } from "../utils/aiagent/aiAgentUtils";
import SegmentedProgressBar from "./ui/SegmentedProgressBar";
import { GlobalToggle } from "./drawer/aiAgent/AiAgent";
import { selectCid } from "../features/companyid";
import { getSpecialTab, getUserName, getUserEmail } from "../features/userid";
import { sendUpgradeEmail } from "../utils/sendUpgradeEmail";
import { toast } from "react-toastify";

const UpgradeBanner = () => {
  const userName = useSelector(getUserName);
  const userEmail = useSelector(getUserEmail);
  const companyId = useSelector(selectCid);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleContact = async () => {
    if (sending || sent) return;
    setSending(true);
    try {
      await sendUpgradeEmail({ userName, companyId, userEmail });
      setSent(true);
      toast.success("Email sent! Our team will reach out to you shortly.");
    } catch (err) {
      toast.error("Failed to send email. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-xl p-[2px] bg-gradient-to-r from-[#148EFF] to-[#FF8BE4] mb-6">
      <div className="bg-white rounded-[10px] p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.4372 10.0828L11.4075 8.59375L9.9231 4.56094C9.83519 4.32213 9.67616 4.11604 9.46744 3.97046C9.25873 3.82488 9.01038 3.74682 8.75591 3.74682C8.50144 3.74682 8.25309 3.82488 8.04438 3.97046C7.83566 4.11604 7.67663 4.32213 7.58872 4.56094L6.09497 8.59375L2.06216 10.0781C1.82336 10.166 1.61726 10.3251 1.47168 10.5338C1.3261 10.7425 1.24805 10.9908 1.24805 11.2453C1.24805 11.4998 1.3261 11.7481 1.47168 11.9568C1.61726 12.1656 1.82336 12.3246 2.06216 12.4125L6.09497 13.9062L7.57935 17.9391C7.66725 18.1779 7.82629 18.384 8.035 18.5295C8.24372 18.6751 8.49207 18.7532 8.74654 18.7532C9.00101 18.7532 9.24935 18.6751 9.45807 18.5295C9.66678 18.384 9.82582 18.1779 9.91372 17.9391L11.4075 13.9062L15.4403 12.4219C15.6791 12.334 15.8852 12.1749 16.0308 11.9662C16.1763 11.7575 16.2544 11.5092 16.2544 11.2547C16.2544 11.0002 16.1763 10.7519 16.0308 10.5432C15.8852 10.3344 15.6791 10.1754 15.4403 10.0875L15.4372 10.0828Z" fill="#148EFF" />
            </svg>
            <p className="text-base font-semibold text-primaryBlack">Still Doing Traditional Search?</p>
          </div>
          <p className="text-sm text-primaryBlack/50">
            Find qualified candidates with Northstarz assisted sourcing.
          </p>
        </div>
        <button
          onClick={handleContact}
          disabled={sending || sent}
          className="whitespace-nowrap text-white font-medium text-sm px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#148EFF] to-[#FF8BE4] hover:shadow-lg transition-shadow cursor-pointer disabled:opacity-60"
        >
          {sent ? "Request Submitted \u2014 Our Team Will Reach Out Shortly" : sending ? "Sending..." : "Unlock Sia"}
        </button>
      </div>
    </div>
  );
};

const StarSvg = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clip-path="url(#clip0_23_5480)">
      <path
        d="M10.8054 7.05797L7.98464 6.01562L6.94558 3.19266C6.88405 3.02549 6.77272 2.88123 6.62662 2.77932C6.48052 2.67742 6.30668 2.62278 6.12855 2.62278C5.95042 2.62278 5.77658 2.67742 5.63048 2.77932C5.48438 2.88123 5.37305 3.02549 5.31152 3.19266L4.2659 6.01562L1.44293 7.05469C1.27576 7.11622 1.1315 7.22755 1.02959 7.37365C0.927685 7.51975 0.873047 7.69359 0.873047 7.87172C0.873047 8.04985 0.927685 8.22369 1.02959 8.36979C1.1315 8.51589 1.27576 8.62722 1.44293 8.68875L4.2659 9.73438L5.30496 12.5573C5.36649 12.7245 5.47782 12.8688 5.62392 12.9707C5.77002 13.0726 5.94386 13.1272 6.12199 13.1272C6.30012 13.1272 6.47396 13.0726 6.62006 12.9707C6.76616 12.8688 6.87749 12.7245 6.93902 12.5573L7.98464 9.73438L10.8076 8.69531C10.9748 8.63378 11.119 8.52245 11.221 8.37635C11.3229 8.23025 11.3775 8.05641 11.3775 7.87828C11.3775 7.70015 11.3229 7.52631 11.221 7.38021C11.119 7.23411 10.9748 7.12278 10.8076 7.06125L10.8054 7.05797ZM7.49246 8.98078C7.43307 9.00267 7.37914 9.03718 7.33439 9.08193C7.28964 9.12668 7.25513 9.18061 7.23324 9.24L6.12527 12.2418L5.01949 9.24219C4.99764 9.18218 4.96291 9.12768 4.91775 9.08252C4.87259 9.03736 4.81809 9.00263 4.75808 8.98078L1.75847 7.875L4.75808 6.76922C4.81809 6.74737 4.87259 6.71264 4.91775 6.66748C4.96291 6.62232 4.99764 6.56782 5.01949 6.50781L6.12527 3.5082L7.23105 6.50781C7.25294 6.5672 7.28745 6.62113 7.3322 6.66588C7.37695 6.71063 7.43088 6.74514 7.49027 6.76703L10.4921 7.875L7.49246 8.98078ZM7.87527 2.1875C7.87527 2.07147 7.92136 1.96019 8.00341 1.87814C8.08546 1.79609 8.19674 1.75 8.31277 1.75H9.18777V0.875C9.18777 0.758968 9.23386 0.647688 9.31591 0.565641C9.39796 0.483594 9.50924 0.4375 9.62527 0.4375C9.7413 0.4375 9.85258 0.483594 9.93463 0.565641C10.0167 0.647688 10.0628 0.758968 10.0628 0.875V1.75H10.9378C11.0538 1.75 11.1651 1.79609 11.2471 1.87814C11.3292 1.96019 11.3753 2.07147 11.3753 2.1875C11.3753 2.30353 11.3292 2.41481 11.2471 2.49686C11.1651 2.57891 11.0538 2.625 10.9378 2.625H10.0628V3.5C10.0628 3.61603 10.0167 3.72731 9.93463 3.80936C9.85258 3.89141 9.7413 3.9375 9.62527 3.9375C9.50924 3.9375 9.39796 3.89141 9.31591 3.80936C9.23386 3.72731 9.18777 3.61603 9.18777 3.5V2.625H8.31277C8.19674 2.625 8.08546 2.57891 8.00341 2.49686C7.92136 2.41481 7.87527 2.30353 7.87527 2.1875ZM13.5628 4.8125C13.5628 4.92853 13.5167 5.03981 13.4346 5.12186C13.3526 5.20391 13.2413 5.25 13.1253 5.25H12.6878V5.6875C12.6878 5.80353 12.6417 5.91481 12.5596 5.99686C12.4776 6.07891 12.3663 6.125 12.2503 6.125C12.1342 6.125 12.023 6.07891 11.9409 5.99686C11.8589 5.91481 11.8128 5.80353 11.8128 5.6875V5.25H11.3753C11.2592 5.25 11.148 5.20391 11.0659 5.12186C10.9839 5.03981 10.9378 4.92853 10.9378 4.8125C10.9378 4.69647 10.9839 4.58519 11.0659 4.50314C11.148 4.42109 11.2592 4.375 11.3753 4.375H11.8128V3.9375C11.8128 3.82147 11.8589 3.71019 11.9409 3.62814C12.023 3.54609 12.1342 3.5 12.2503 3.5C12.3663 3.5 12.4776 3.54609 12.5596 3.62814C12.6417 3.71019 12.6878 3.82147 12.6878 3.9375V4.375H13.1253C13.2413 4.375 13.3526 4.42109 13.4346 4.50314C13.5167 4.58519 13.5628 4.69647 13.5628 4.8125Z"
        fill="#148EFF"
      />
    </g>
    <defs>
      <clipPath id="clip0_23_5480">
        <rect width="14" height="14" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const tableColumns = (onsort, sortBy) => [
  {
    header: (
      <div className="flex items-start gap-1">
        Name <StarSvg />
      </div>
    ),
    key: "name",
    id: "name",
    notCentered: true,
    body: (item) => (
      <CardTooltip tooltip={<CandidateCard data={item} />}>
        {item.linkedin ? (
          <Link
            to={getNewPageLink(item.linkedin)}
            target="_blank"
            className="h-full flex flex-col"
          >
            <div className="text-left text-web-blue font-semibold flex items-center gap-1">
              {" "}
              {item.name} <FaLinkedin className="text-web-blue" />
            </div>
            <div className="text-left text-primaryBlack text-[10px]">
              {item.currentJobtitle}
            </div>
          </Link>
        ) : (
          <div className="h-full flex flex-col">
            <div className="text-left text-web-blue font-semibold flex items-center gap-1">
              {" "}
              {item.name}
            </div>
            <div className="text-left text-primaryBlack text-[10px]">
              {item.currentJobtitle}
            </div>
          </div>
        )}
      </CardTooltip>
    ),
  },
  {
    header: "Skills",
    key: "skills",
    id: "skills",
    body: (item) => (
      <div className="max-w-[130px] mx-auto text-[10px] line-clamp-3 text-ellipsis">
        {" "}
        {item.extracted_skills.join(", ")}{" "}
      </div>
    ),
  },
  {
    header: "YOE",
    key: "YOE",
    id: "experience",
    sortable: true,
    sortFn: () =>
      sortBy === "YOE" ? onsort("sortby", "") : onsort("sortby", "YOE"),
    body: (item) => item.totalexperience,
  },
  {
    header: "Highest Education",
    key: "highestEducation",
    id: "highestEducation",
    body: (item) => item.highestqualification,
  },
  {
    header: "Source",
    key: "source",
    id: "source",
    sortable: true,
    sortFn: () =>
      sortBy === "source" ? onsort("sortby", "") : onsort("sortby", "source"),
    body: (item) =>
      item.source === "Northstarz" ? (
        <div className="flex items-center gap-1 justify-center text-web-blue">
          {" "}
          {item.source} <StarSvg />
        </div>
      ) : (
        item.source
      ),
  },
  {
    header: "Status",
    key: "status",
    id: "status",
    body: (item) => (
      <div className="mx-auto w-fit">
        <Tag
          text={STATUS_TO_CANDIDATE_MAPPER[item.status]?.label || "success"}
          variant={
            STATUS_TO_CANDIDATE_MAPPER[item.status]?.variant || "success"
          }
          tooltip={item.reason}
        />
      </div>
    ),
  },
];

const SORT_BY_TO_KEY = {
  YOE: "totalexperience",
  source: "source",
  "": "",
};

const OutreachedCandidates = () => {
  const location = useLocation();
  const { query, setQueryParam } = useQueryParams();
  const navigate = useNavigate();

  const id = location.state?.id;
  const companyId = useSelector(selectCid);
  const specialTab = useSelector(getSpecialTab);

  const pageKey = "outreachedCandidate" + id + query.outreachedCandidate;

  const { page, page_size } = useSelector((state) =>
    selectCustomPagination(state, pageKey)
  );

  const { data, isLoading, refetch } = useFetch(
    `/api/v1/sourced-candidates/${id}` +
    "?current_page=" +
    page +
    "&candidates_per_page=" +
    page_size +
    "&sort_by=" +
    SORT_BY_TO_KEY[query.sortby || ""]
    + `&sort_order=asc&status=${OUTREACHED_TABS_STATUS_MAPPER[query.outreachedCandidate] ?? 0}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  const candidates = data?.candidates || [];
  const outreachCount = data?.outreach || 0;
  const appliedCount = data?.applied || 0;

  useEffect(() => {
    if (isLoading || !data) return;
    const currentTab = query.outreachedCandidate;
    if (!currentTab || currentTab === "outreached") {
      if (outreachCount === 0 && appliedCount > 0) {
        setQueryParam("outreachedCandidate", "applied");
      }
    } else if (currentTab === "applied") {
      if (appliedCount === 0 && outreachCount > 0) {
        setQueryParam("outreachedCandidate", "outreached");
      }
    }
  }, [isLoading, data]);

  const finalStatus = REVERSE_STATUS_MAPPER.ONGOING === data?.status;

  const { refetch: toggleAutoSourcing } = useFetch(
    "/api/v1/toggle-auto-sourcing",
    {
      method: "post",
      data: {
        job_uuid: id,
        sourcing_active: !finalStatus,
      },
    },
    false
  );

  const onsort = (key, value) => {
    setQueryParam(key, value);
    refetch();
  };

  if (!isLoading && outreachCount === 0 && appliedCount === 0) {
    return (
      <div className="w-fit mx-auto mt-4">
        <EmptyScreen>
          <div className="flex flex-col items-center">
            <div className="flex flex-col items-center gap-2">
              <p className="text-3xl font-semibold text-primaryBlack">
                Good things take time!
              </p>
              <p className="text-sm text-primaryBlack/50">
                Have patience, we’re currently reaching out to potential
                candidates on your behalf.
              </p>
            </div>
            <Button
              variant="secondary"
              className="mt-4"
              onClick={() => navigate(REDIRECTION_LINKS.outreach)}
            >
              Go Back
            </Button>
          </div>
        </EmptyScreen>
      </div>
    );
  }

  return (
    <div>
      {!specialTab && <div className="px-6 pt-4"><UpgradeBanner /></div>}
      <PageLayout
        title={data?.job_title || "Loading..."}
        className={"px-0"}
        backLink={REDIRECTION_LINKS.outreach}
        rightSideElementsClassName={"w-fit"}
        rightSideElements={
          <div className="flex items-center gap-3 w-[auto]">
            <div className="text-white bg-gradient-to-r from-[#148EFF] to-[#FF8BE4] border-none hover:shadow-md p-0.5 rounded-lg">
              <GlobalToggle className="bg-white !py-1.5 p-0" variant="textBased" companyId={companyId} jobId={id}>
                <p className="w-fit flex gap-1"> <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.4372 10.0828L11.4075 8.59375L9.9231 4.56094C9.83519 4.32213 9.67616 4.11604 9.46744 3.97046C9.25873 3.82488 9.01038 3.74682 8.75591 3.74682C8.50144 3.74682 8.25309 3.82488 8.04438 3.97046C7.83566 4.11604 7.67663 4.32213 7.58872 4.56094L6.09497 8.59375L2.06216 10.0781C1.82336 10.166 1.61726 10.3251 1.47168 10.5338C1.3261 10.7425 1.24805 10.9908 1.24805 11.2453C1.24805 11.4998 1.3261 11.7481 1.47168 11.9568C1.61726 12.1656 1.82336 12.3246 2.06216 12.4125L6.09497 13.9062L7.57935 17.9391C7.66725 18.1779 7.82629 18.384 8.035 18.5295C8.24372 18.6751 8.49207 18.7532 8.74654 18.7532C9.00101 18.7532 9.24935 18.6751 9.45807 18.5295C9.66678 18.384 9.82582 18.1779 9.91372 17.9391L11.4075 13.9062L15.4403 12.4219C15.6791 12.334 15.8852 12.1749 16.0308 11.9662C16.1763 11.7575 16.2544 11.5092 16.2544 11.2547C16.2544 11.0002 16.1763 10.7519 16.0308 10.5432C15.8852 10.3344 15.6791 10.1754 15.4403 10.0875L15.4372 10.0828ZM10.7043 12.8297C10.6195 12.861 10.5425 12.9103 10.4785 12.9742C10.4146 13.0381 10.3653 13.1152 10.334 13.2L8.75122 17.4883L7.17154 13.2031C7.14032 13.1174 7.09071 13.0395 7.0262 12.975C6.96168 12.9105 6.88383 12.8609 6.7981 12.8297L2.51294 11.25L6.7981 9.67031C6.88383 9.6391 6.96168 9.58949 7.0262 9.52497C7.09071 9.46046 7.14032 9.3826 7.17154 9.29688L8.75122 5.01172L10.3309 9.29688C10.3622 9.38171 10.4115 9.45875 10.4754 9.52269C10.5393 9.58662 10.6164 9.63592 10.7012 9.66719L14.9895 11.25L10.7043 12.8297ZM11.2512 3.125C11.2512 2.95924 11.3171 2.80027 11.4343 2.68306C11.5515 2.56585 11.7105 2.5 11.8762 2.5H13.1262V1.25C13.1262 1.08424 13.1921 0.925268 13.3093 0.808058C13.4265 0.690848 13.5855 0.625 13.7512 0.625C13.917 0.625 14.076 0.690848 14.1932 0.808058C14.3104 0.925268 14.3762 1.08424 14.3762 1.25V2.5H15.6262C15.792 2.5 15.951 2.56585 16.0682 2.68306C16.1854 2.80027 16.2512 2.95924 16.2512 3.125C16.2512 3.29076 16.1854 3.44973 16.0682 3.56694C15.951 3.68415 15.792 3.75 15.6262 3.75H14.3762V5C14.3762 5.16576 14.3104 5.32473 14.1932 5.44194C14.076 5.55915 13.917 5.625 13.7512 5.625C13.5855 5.625 13.4265 5.55915 13.3093 5.44194C13.1921 5.32473 13.1262 5.16576 13.1262 5V3.75H11.8762C11.7105 3.75 11.5515 3.68415 11.4343 3.56694C11.3171 3.44973 11.2512 3.29076 11.2512 3.125ZM19.3762 6.875C19.3762 7.04076 19.3104 7.19973 19.1932 7.31694C19.076 7.43415 18.917 7.5 18.7512 7.5H18.1262V8.125C18.1262 8.29076 18.0604 8.44973 17.9432 8.56694C17.826 8.68415 17.667 8.75 17.5012 8.75C17.3355 8.75 17.1765 8.68415 17.0593 8.56694C16.9421 8.44973 16.8762 8.29076 16.8762 8.125V7.5H16.2512C16.0855 7.5 15.9265 7.43415 15.8093 7.31694C15.6921 7.19973 15.6262 7.04076 15.6262 6.875C15.6262 6.70924 15.6921 6.55027 15.8093 6.43306C15.9265 6.31585 16.0855 6.25 16.2512 6.25H16.8762V5.625C16.8762 5.45924 16.9421 5.30027 17.0593 5.18306C17.1765 5.06585 17.3355 5 17.5012 5C17.667 5 17.826 5.06585 17.9432 5.18306C18.0604 5.30027 18.1262 5.45924 18.1262 5.625V6.25H18.7512C18.917 6.25 19.076 6.31585 19.1932 6.43306C19.3104 6.55027 19.3762 6.70924 19.3762 6.875Z" fill="#51A2FF" />
                </svg>
                  Ask Sia Out </p>
              </GlobalToggle>
            </div>
            <Button
              variant={finalStatus ? "dangerLite" : "secondary"}
              onClick={() => toggleAutoSourcing().then(() => window.location.reload())}
            >
              <p >
                {finalStatus ? "Stop Outreach" : "Resume Outreach"}
              </p>
            </Button>
          </div>
        }
      >
        <PageCard className={"!px-0"}>
          <div className="flex gap-24 px-3">
            <Tabs
              tabKey="outreachedCandidate"
              tabs={OUTREACHED_TABS}
              showCount
              allCounts={{
                outreached: data?.outreach || 0,
                applied: data?.applied || 0,
              }}
              className={"whitespace-nowrap"}
              tabClass={"min-w-[200px]"}
            />
            <SegmentedProgressBar data={getNewSourcedCandidateObject(data?.source_breakdown) || []} />
          </div>

          {isLoading ? (
            <div className="w-fit mx-auto mt-4">
              <TailSpin color="blue" height={80} width={80} />
            </div>
          ) : (
            <>
              <CustomTable
                tableData={candidates}
                columns={tableColumns(onsort, query.sortby)}
                className={"!px-0"}
              />
              <Pagination
                pageKey={pageKey}
                totalPages={data?.pagination?.total_pages || 0}
              />
            </>
          )}
        </PageCard>
      </PageLayout>
    </div>
  );
};

export default OutreachedCandidates;
